import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  Select,
  SkeletonList,
  Switch,
} from '@/components/ui'
import { QueryBoundary } from '@/components/domain'
import { LANGUAGE_LABEL } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { useLanguageSettings } from '../hooks'
import type { LanguageCode, LanguageSettings as Languages } from '@/types'

const SELECTABLE: LanguageCode[] = ['en-IN', 'hi-IN', 'en-US', 'mr-IN', 'ta-IN', 'te-IN']

/**
 * Auto-detect is the default and the honest one for this market: a workspace
 * that speaks Hinglish is not choosing between English and Hindi, it uses both
 * inside one sentence. The pinned option is offered with its downside attached
 * rather than presented as the safer choice.
 */
export function LanguageSettings() {
  const t = useT()
  const { query, save } = useLanguageSettings()
  const [draft, setDraft] = useState<Languages | null>(null)

  useEffect(() => {
    if (query.data) setDraft(query.data)
  }, [query.data])

  const toggle = (code: LanguageCode) => {
    if (!draft) return
    const candidates = draft.candidates.includes(code)
      ? draft.candidates.filter((c) => c !== code)
      : [...draft.candidates, code]
    setDraft({ ...draft, candidates })
  }

  const noCandidates = draft?.mode === 'identify' && draft.candidates.length === 0

  return (
    <QueryBoundary
      isLoading={query.isPending || !draft}
      error={query.error}
      onRetry={query.refetch}
      skeleton={<SkeletonList rows={4} className="sb-panel p-4" />}
    >
      {draft && (
        <div className="space-y-4">
          <Card>
            <CardHeader
              title={t('langSettings.transcriptionTitle')}
              description={t('langSettings.transcriptionDesc')}
            />
            <CardBody className="space-y-4">
              <Select
                label={t('langSettings.detectionMode')}
                value={draft.mode}
                onChange={(event) => setDraft({ ...draft, mode: event.target.value as Languages['mode'] })}
                options={[
                  { value: 'identify', label: t('langSettings.mode.identify') },
                  { value: 'pinned', label: t('langSettings.mode.pinned') },
                ]}
              />

              {draft.mode === 'identify' ? (
                <fieldset>
                  <legend className="mb-2 text-sm font-medium text-fg">{t('langSettings.candidateLanguages')}</legend>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {SELECTABLE.map((code) => (
                      <Checkbox
                        key={code}
                        label={LANGUAGE_LABEL[code]}
                        description={code}
                        checked={draft.candidates.includes(code)}
                        onChange={() => toggle(code)}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-fg-subtle">
                    {t('langSettings.candidateHelp')}
                  </p>
                  {noCandidates && (
                    <p className="mt-2 text-sm text-danger">{t('langSettings.selectAtLeastOne')}</p>
                  )}
                </fieldset>
              ) : (
                <Select
                  label={t('langSettings.language')}
                  value={draft.pinned ?? 'en-IN'}
                  onChange={(event) => setDraft({ ...draft, pinned: event.target.value as LanguageCode })}
                  options={SELECTABLE.map((code) => ({ value: code, label: LANGUAGE_LABEL[code] }))}
                />
              )}
            </CardBody>
            <CardFooter>
              <Button
                variant="primary"
                disabled={noCandidates}
                isLoading={save.isPending}
                onClick={() =>
                  save.mutate({
                    mode: draft.mode,
                    candidates: draft.candidates,
                    pinned: draft.mode === 'pinned' ? (draft.pinned ?? 'en-IN') : null,
                  })
                }
              >
                {t('common.saveChanges')}
              </Button>
            </CardFooter>
          </Card>

          {draft.mode === 'pinned' && (
            <Alert tone="warning" title={t('langSettings.codeSwitchTitle')}>
              {t('langSettings.codeSwitchBody')}
            </Alert>
          )}

          {/*
            Dropped by accident while this pane was being translated: the card
            went, its five translation keys stayed. `flagUntranslated` is a
            LIVE setting — `api/workspace.py` accepts it, `postgres_workspace`
            writes it, `domain.ts` types it — so deleting the only control that
            sets it left a workspace unable to turn the untranslated-summary
            banner on or off, with nothing in the product to say why.
          */}
          <Card>
            <CardHeader title={t('langSettings.summaryOutput')} />
            <CardBody className="space-y-4">
              <Switch
                label={t('langSettings.alwaysEnglish')}
                description={t('langSettings.alwaysEnglishDesc')}
                showLabel
                checked={draft.summaryLanguage === 'en'}
                disabled
                onChange={() => undefined}
              />
              <Switch
                label={t('langSettings.flagUntranslated')}
                description={t('langSettings.flagUntranslatedDesc')}
                showLabel
                checked={draft.flagUntranslated}
                onChange={(flagUntranslated) => {
                  setDraft({ ...draft, flagUntranslated })
                  save.mutate({ flagUntranslated })
                }}
              />
            </CardBody>
          </Card>
        </div>
      )}
    </QueryBoundary>
  )
}
